import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../../supabaseClient";
// import ShieldImg from "../../Assets/Icons/shield.png";
import LoaderBox from "./LoaderBox";

const HomeRaspored = ({ turnirId }) => {
  const [daniLista, setDaniLista] = useState([]);
  const [activeDan, setActiveDan] = useState("");
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hideDataMode, setHideDataMode] = useState(false);
  const [isAutoScrolling, setIsAutoScrolling] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.has("highlight");
  });

  // Formatira datum u npr. "Pon 27.07." da se točno podudara s formatom u bazi
  const formatDateToDayString = (dateObj) => {
    const daniTjedna = ["Ned", "Pon", "Uto", "Sri", "Čet", "Pet", "Sub"];
    const dayName = daniTjedna[dateObj.getDay()];
    const dayNum = String(dateObj.getDate()).padStart(2, "0");
    const monthNum = String(dateObj.getMonth() + 1).padStart(2, "0");
    return `${dayName} ${dayNum}.${monthNum}.`;
  };

  const fetchMatches = useCallback(async () => {
    if (!turnirId) return;
    setLoading(true);
    try {
      // 1. Dohvat postavki turnira
      const { data: settings } = await supabase
        .from("admin_turniri")
        .select("prikazi_raspored, hide_data_mode, datum_od, datum_do")
        .eq("id", turnirId)
        .single();

      if (settings) {
        const isLocalhost =
          window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1";
        setHideDataMode(
          isLocalhost
            ? false
            : !settings.prikazi_raspored || settings.hide_data_mode,
        );
      }

      // 2. Dohvat utakmica preko tournament_id
      // 2. Dohvat regularnih i veteranskih utakmica preko tournament_id
      const { data: regData, error: regError } = await supabase
        .from("turniri_utakmice")
        .select(
          `
          *,
          team_a:team_a_id(name, logo_url),
          team_b:team_b_id(name, logo_url)
        `,
        )
        .eq("tournament_id", turnirId);

      if (regError) throw regError;

      const { data: vetData, error: vetError } = await supabase
        .from("turniri_veterani")
        .select(
          `
          *,
          team_a:team_a_id(name, logo_url),
          team_b:team_b_id(name, logo_url)
        `,
        )
        .eq("tournament_id", turnirId);

      if (vetError) throw vetError;

      // Filtriramo regularne podatke da uklonimo višestruke duplikate završnice (prema bracket_slot_id)
      const uniqueRegMap = new Map();
      (regData || []).forEach((m) => {
        if (m.bracket_slot_id) {
          const key = `slot-${m.bracket_slot_id}`;
          if (uniqueRegMap.has(key)) {
            const existing = uniqueRegMap.get(key);
            // Prednost dajemo onom zapisu koji ima popunjene timove ili je označen kao završen
            if (
              (!existing.team_a_id && m.team_a_id) ||
              (!existing.team_b_id && m.team_b_id) ||
              m.is_completed
            ) {
              uniqueRegMap.set(key, m);
            }
          } else {
            uniqueRegMap.set(key, m);
          }
        } else {
          // Regularne utakmice po grupama
          uniqueRegMap.set(`reg-${m.team_a_id}-${m.team_b_id}`, m);
        }
      });

      const formattedReg = Array.from(uniqueRegMap.values()).map((m) => ({
        ...m,
        isVeteran: false,
      }));

      // Isto to možemo primijeniti i na veteranske utakmice zatreba li
      const uniqueVetMap = new Map();
      (vetData || []).forEach((m) => {
        if (m.bracket_slot_id) {
          const key = `vet-slot-${m.bracket_slot_id}`;
          if (uniqueVetMap.has(key)) {
            const existing = uniqueVetMap.get(key);
            if (
              (!existing.team_a_id && m.team_a_id) ||
              (!existing.team_b_id && m.team_b_id) ||
              m.is_completed
            ) {
              uniqueVetMap.set(key, m);
            }
          } else {
            uniqueVetMap.set(key, m);
          }
        } else {
          uniqueVetMap.set(`vet-${m.team_a_id}-${m.team_b_id}`, m);
        }
      });

      const formattedVet = Array.from(uniqueVetMap.values()).map((m) => ({
        ...m,
        isVeteran: true,
      }));

      const fetchedMatches = [...formattedReg, ...formattedVet].sort((a, b) => {
        if (!a.match_time) return 1;
        if (!b.match_time) return -1;
        return a.match_time.localeCompare(b.match_time);
      });

      setMatches(fetchedMatches);

      // 3. Generiranje svih dana unutar raspona datum_od i datum_do
      let generatedDays = [];
      if (settings?.datum_od && settings?.datum_do) {
        let curr = new Date(settings.datum_od);
        let end = new Date(settings.datum_do);
        while (curr <= end) {
          generatedDays.push({
            dateObj: new Date(curr),
            label: formatDateToDayString(curr),
          });
          curr.setDate(curr.getDate() + 1);
        }
      }

      // Ako nema definiranog raspona, uzimamo dane iz utakmica
      if (generatedDays.length === 0) {
        const uniqueRawDays = [
          ...new Set(fetchedMatches.map((m) => m.match_day).filter(Boolean)),
        ];
        uniqueRawDays.forEach((dStr) => {
          generatedDays.push({
            dateObj: new Date(),
            label: dStr,
          });
        });
      }

      // Sortiramo dane kronološki od najstarijeg prema najnovijem
      generatedDays.sort((a, b) => a.dateObj - b.dateObj);

      const finalDaysList = generatedDays.map((d) => d.label);

      setDaniLista(finalDaysList);

      if (finalDaysList.length > 0) {
        const queryParams = new URLSearchParams(window.location.search);
        const highlight = queryParams.get("highlight");

        if (highlight) {
          const targetTeam = decodeURIComponent(highlight);
          const match = fetchedMatches.find(
            (m) =>
              m.team_a?.name === targetTeam || m.team_b?.name === targetTeam,
          );

          if (match && match.match_day) {
            setActiveDan(match.match_day);
          } else {
            setActiveDan(finalDaysList[0]);
          }
        } else {
          const danSPreostalimUtakmicama = finalDaysList.find((dan) =>
            fetchedMatches.some((m) => m.match_day === dan && !m.is_completed),
          );

          if (danSPreostalimUtakmicama) {
            setActiveDan(danSPreostalimUtakmicama);
          } else {
            setActiveDan(finalDaysList[0]);
          }
        }
      }
    } catch (err) {
      console.error("❌ Greška pri dohvaćanju rasporeda:", err.message);
    } finally {
      setLoading(false);
    }
  }, [turnirId]);

  useEffect(() => {
    const handleUrlChange = () => {
      const params = new URLSearchParams(window.location.search);
      if (params.has("highlight")) {
        setIsAutoScrolling(true);
      }
    };

    window.addEventListener("urlChanged", handleUrlChange);
    return () => window.removeEventListener("urlChanged", handleUrlChange);
  }, []);

  useEffect(() => {
    if (!isAutoScrolling || loading || matches.length === 0) return;

    const queryParams = new URLSearchParams(window.location.search);
    const highlight = queryParams.get("highlight");
    if (!highlight) return;

    const highlightTeam = decodeURIComponent(highlight);
    const matchToScroll = matches.find(
      (m) =>
        m.team_a?.name === highlightTeam || m.team_b?.name === highlightTeam,
    );

    if (matchToScroll) {
      if (matchToScroll.match_day && matchToScroll.match_day !== activeDan) {
        setActiveDan(matchToScroll.match_day);
        return;
      }

      setTimeout(() => {
        const element = document.getElementById(`match-${matchToScroll.id}`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          element.classList.add("highlight-flash");
          setTimeout(() => element.classList.remove("highlight-flash"), 2000);
        }

        window.history.replaceState(
          {},
          document.title,
          window.location.pathname,
        );
        setIsAutoScrolling(false);
      }, 150);
    } else {
      setIsAutoScrolling(false);
    }
  }, [loading, matches, activeDan, isAutoScrolling]);

  useEffect(() => {
    fetchMatches();
  }, [fetchMatches]);

  // Filtriramo utakmice za aktivni dan (uklonio sam !m.is_completed privremeno ako želiš vidjeti i završene, ili vrati nazad po želji)
  // Filtriramo utakmice za aktivni dan i mičemo one koje su završene (!m.is_completed)
  const displayedMatches = hideDataMode
    ? []
    : matches.filter((m) => {
        const matchDan = String(m.match_day || "").trim();
        const currentDan = String(activeDan || "").trim();
        return matchDan === currentDan && !m.is_completed;
      });

  // Provjera jesu li sve utakmice za aktivni dan završene
  const danUtakmice = matches.filter(
    (m) => String(m.match_day || "").trim() === String(activeDan || "").trim(),
  );
  const sveUtakmiceDanaZavrsene =
    danUtakmice.length > 0 && danUtakmice.every((m) => m.is_completed);

  const sveUtakmiceZavrsene =
    matches.length > 0 && matches.every((m) => m.is_completed);

  if (loading) return <LoaderBox text="Učitavanje rasporeda..." />;

  return (
    <div className="home-raspored-wrapper">
      {/* Navigacija danima s datumima (kronološki sortirano) */}
      <div className="day-navigation-tabs">
        {daniLista.map((d) => {
          const isDayFinished =
            matches.length > 0 &&
            matches.filter((m) => m.match_day === d).length > 0 &&
            matches
              .filter((m) => m.match_day === d)
              .every((m) => m.is_completed);

          return (
            <button
              key={d}
              className={`${activeDan === d ? "active" : ""} ${isDayFinished ? "day-done" : ""}`}
              onClick={() => {
                setIsAutoScrolling(false);
                setActiveDan(d);
              }}
            >
              {d}
            </button>
          );
        })}
      </div>

      <div className="schedule-content">
        {sveUtakmiceZavrsene ? (
          <div className="raspored-empty">
            <div className="icon-wrapper">
              <i className="fi fi-rs-trophy"></i>
            </div>
            <h3>Turnir je završen!</h3>
            <p>
              Sve utakmice su odigrane. Rezultate pogledajte u tabu Rezultati.
            </p>
          </div>
        ) : sveUtakmiceDanaZavrsene ? (
          <div className="raspored-empty">
            <div className="icon-wrapper">
              <i className="fi fi-rs-calendar-check"></i>
            </div>
            <h3>Sve utakmice su završene za ovaj dan!</h3>
            <p>Odaberite drugi dan ili pogledajte rezultate.</p>
          </div>
        ) : displayedMatches.length > 0 ? (
          <div className="matches-grid">
            {displayedMatches.map((m) => (
              <div
                key={m.id || `${m.team_a_id}-${m.team_b_id}`}
                id={`match-${m.id}`}
                className="match-card-modern"
                style={{ position: "relative" }}
              >
                {/* Oznaka za veterane u kutu kartice */}
                {m.isVeteran && (
                  <span className="veteran-corner-badge">VETERANI</span>
                )}

                <div className="match-top-info">
                  <span className="group-badge">
                    {m.group_name === "Završnica"
                      ? "Završnica"
                      : `Grupa ${m.group_name}`}
                  </span>
                  <span className="time-badge">{m.match_time}</span>
                </div>

                <div className="teams-display">
                  <div className="team-box">
                    <span className="team-name">{m.team_a?.name}</span>
                  </div>
                  <div className="vs-circle">VS</div>
                  <div className="team-box">
                    <span className="team-name">{m.team_b?.name}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default HomeRaspored;
